import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo, humanizeSlug } from "@/lib/seo";
import { useState } from "react";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useTaxRates } from "@/hooks/use-query-hooks";
import { formatCurrency } from "@/lib/format";
import { Calculator, Loader2, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/tools/tax-calculator/$stateSlug")({
  head: ({ params }) => {
    const state = humanizeSlug(params.stateSlug);
    return buildSeo({
      title: `${state} Lottery Tax Calculator — Take-Home After Taxes | USA Lottery Club`,
      description: `Estimate your ${state} lottery winnings after federal and state taxes. Compare lump-sum vs annuity payouts for Powerball, Mega Millions, and ${state} state games.`,
      path: `/tools/tax-calculator/${params.stateSlug}`,
    });
  },
  component: StateTaxCalculator,
});

function StateTaxCalculator() {
  const { stateSlug } = Route.useParams();
  const { data: taxRates, isLoading } = useTaxRates();
  const [jackpotStr, setJackpotStr] = useState("");
  const [isLumpSum, setIsLumpSum] = useState(true);
  const [result, setResult] = useState<{ gross: number; federal: number; state: number; net: number } | null>(null);

  const taxRate = taxRates?.find((t) => t.states?.slug === stateSlug);
  const stateName = taxRate?.states?.name;
  const stateAbbr = taxRate?.states?.abbreviation;

  function calculate() {
    const jackpot = parseFloat(jackpotStr.replace(/[,$]/g, ""));
    if (isNaN(jackpot) || !taxRate) return;
    const gross = isLumpSum ? jackpot * 0.6 : jackpot;
    const federal = gross * (taxRate.federal_tax_rate / 100);
    const stateTax = gross * (Number(taxRate.state_tax_rate) / 100);
    setResult({ gross, federal, state: stateTax, net: gross - federal - stateTax });
  }

  return (
    <Layout>
      <SEOHead
        title={stateName ? `${stateName} Lottery Tax Calculator` : "Lottery Tax Calculator"}
        description={stateName ? `Calculate your lottery winnings after ${stateName} state tax and federal tax.` : "Calculate lottery winnings after taxes."}
      />
      <div className="container py-6 md:py-10 max-w-2xl">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/tools">Tools</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/tools/tax-calculator">Tax Calculator</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{stateName || "..."}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : !taxRate ? (
          <div className="py-12 text-center">
            <p className="text-muted-foreground mb-4">State not found.</p>
            <Link to="/tools/tax-calculator" className="text-primary hover:underline">Back to Tax Calculator</Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl md:text-3xl font-bold mb-4">
              <Calculator className="inline h-6 w-6 mr-2 text-primary" /> {stateName} Lottery Tax Calculator
            </h1>
            <p className="text-muted-foreground mb-6">
              {stateName} ({stateAbbr}) applies a <strong>{Number(taxRate.state_tax_rate)}% state tax</strong> on lottery winnings, plus a <strong>{taxRate.federal_tax_rate}% federal tax</strong>.
              {taxRate.notes && <span className="block mt-1 text-sm italic">{taxRate.notes}</span>}
            </p>
            <Card className="mb-6">
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label>Jackpot Amount ($)</Label>
                  <Input value={jackpotStr} onChange={(e) => setJackpotStr(e.target.value)} inputMode="numeric" placeholder="e.g. 500000000" />
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={isLumpSum} onCheckedChange={setIsLumpSum} />
                  <Label>{isLumpSum ? "Lump Sum (≈60% of advertised)" : "Annuity (full amount)"}</Label>
                </div>
                <Button onClick={calculate} disabled={!jackpotStr.trim()} className="w-full">
                  <Calculator className="h-4 w-4 mr-2" /> Calculate for {stateName}
                </Button>
              </CardContent>
            </Card>
            {result && (
              <Card className="mb-6">
                <CardContent className="pt-6 space-y-3">
                  <div className="flex justify-between border-b py-2"><span className="text-muted-foreground">{isLumpSum ? "Lump Sum" : "Annuity"}</span><span className="font-bold">{formatCurrency(result.gross)}</span></div>
                  <div className="flex justify-between border-b py-2"><span className="text-muted-foreground">Federal Tax ({taxRate.federal_tax_rate}%)</span><span className="text-destructive font-bold">−{formatCurrency(result.federal)}</span></div>
                  <div className="flex justify-between border-b py-2"><span className="text-muted-foreground">{stateName} State Tax ({Number(taxRate.state_tax_rate)}%)</span><span className="text-destructive font-bold">−{formatCurrency(result.state)}</span></div>
                  <div className="flex justify-between py-2"><span className="font-display font-bold text-lg">Take-Home</span><span className="font-display font-bold text-lg text-success">{formatCurrency(result.net)}</span></div>
                </CardContent>
              </Card>
            )}
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/tools/tax-calculator" className="inline-flex items-center gap-1 text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> All States</Link>
              <Link to="/states/$stateSlug" params={{ stateSlug }} className="text-primary hover:underline">{stateName} Lottery Games →</Link>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}